'use client';
// ═══════════════════════════════════════════════════════════════════════════
// CONGÉ PARENTAL — le chiffre pour SON cas, que personne d'autre ne donne.
// ───────────────────────────────────────────────────────────────────────────
// 📏 Mesuré le 02/09/2026 : six requêtes « congé parental » cumulent 2 597
// affichages pour 27 clics (CTR 1,04 %), positions 6 à 27.
//
// Qui sort devant, vérifié le même jour : l'ONEm, deux agrégateurs
// (MesAllocations, mes-aides-financieres) et deux SaaS (Shyfter, iambeezy).
// Ni Securex, ni Partena, ni UCM.
//
// 🔑 TOUS DÉCRIVENT LE DROIT. AUCUN NE CALCULE. On ne bat pas l'ONEm sur son
// propre texte — et il ne faut pas essayer. On lui prend les lecteurs qui
// veulent un chiffre : « avec mon salaire, ça donne quoi ? »
//
// ⚠️ AUCUN MONTANT ÉCRIT ICI : tout vient de lois-belges via conge-parental-calc.
// ⚠️ ET LE 1/10 AFFICHE SON TROU. lois-belges dit que son barème n'a pas été lu
// à l'ONEm et interdit de le déduire. L'écran écrit donc « non publié ici » et
// renvoie à l'ONEm — c'est moins satisfaisant qu'un chiffre, et c'est la seule
// chose honnête à faire sur une page que des parents lisent pour décider.
// ═══════════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { simulerCongeParental, formesCongeParental, conditionsCongeParental } from '@/app/lib/conge-parental-calc';
// ⚠️ `euros()` du dépôt, PAS `toLocaleString` : ce composant est rendu côté
// serveur puis réhydraté, et `Intl` n'a pas forcément les mêmes données de
// locale des deux côtés — un écart d'espace insécable suffit à casser
// l'hydratation. Le formateur maison rend la même chaîne partout.
import { euros } from '@/app/lib/conditions-commerciales';

const C = conditionsCongeParental();
const FORMES = formesCongeParental();

const T = {
  fr: {
    titre: 'Avec votre salaire, ça donne quoi ?',
    intro: 'Le calcul utilise les barèmes ONEm, pas une estimation.',
    brut: 'Votre salaire brut mensuel à temps plein',
    forme: 'La formule que vous envisagez',
    salaire: 'Salaire encore payé par l’employeur',
    alloc: 'Allocation ONEm (brut)',
    allocNet: 'dont net',
    total: 'Total brut par mois',
    perte: 'Écart avec votre salaire actuel',
    duree: 'Durée maximale',
    fraction: 'par tranches de',
    mois: 'mois',
    inconnu: 'L’ONEm publie bien cette formule, mais son montant n’a pas été relevé ici. Nous ne le déduisons pas d’une règle de trois : les barèmes ONEm ne sont pas proportionnels. À demander à l’ONEm.',
    vide: 'Indiquez votre brut mensuel pour voir le calcul.',
    cond: `Conditions : enfant de moins de ${C.ageEnfantMax} ans, et ${C.ancienneteMois} mois chez le même employeur sur les ${C.ancienneteFenetreMois} derniers.`,
  },
  nl: {
    titre: 'Wat geeft dat met uw loon?',
    intro: 'De berekening gebruikt de RVA-barema’s, geen schatting.',
    brut: 'Uw bruto maandloon voltijds',
    forme: 'De formule die u overweegt',
    salaire: 'Loon nog betaald door de werkgever',
    alloc: 'RVA-uitkering (bruto)',
    allocNet: 'waarvan netto',
    total: 'Totaal bruto per maand',
    perte: 'Verschil met uw huidige loon',
    duree: 'Maximale duur',
    fraction: 'in schijven van',
    mois: 'maanden',
    inconnu: 'De RVA kent deze formule wel, maar het bedrag is hier niet opgezocht. Wij leiden het niet af met een regel van drie: de RVA-barema’s zijn niet evenredig. Te vragen aan de RVA.',
    vide: 'Geef uw bruto maandloon in om de berekening te zien.',
    cond: `Voorwaarden: kind jonger dan ${C.ageEnfantMax} jaar, en ${C.ancienneteMois} maanden bij dezelfde werkgever in de laatste ${C.ancienneteFenetreMois}.`,
  },
};

// ⚠️ R8 — `lois-belges` ne publie les libellés de formes qu'en FRANÇAIS : c'est
// un registre de droit, pas un fichier de traduction. Sans cette table, le menu
// déroulant de la page néerlandaise affichait « Suspension complète ». La
// traduction vit donc ici, à l'écran, et se clé sur l'`id` — jamais sur le
// libellé français, qui changerait sous nos pieds à la prochaine indexation.
const LABEL_NL = {
  complet: 'Volledige schorsing',
  miTemps: 'Vermindering tot een halftijdse betrekking',
  cinquieme: 'Vermindering met een vijfde',
  dixieme: 'Vermindering met een tiende',
};
const libelle = (f, lang) => (lang === 'nl' ? LABEL_NL[f.id] || f.label : f.label);

const CADRE = { border: '1px solid #c6a34e55', borderRadius: 12, padding: '22px 24px', background: '#c6a34e0d', margin: '32px 0' };
const CHAMP = { width: '100%', padding: '10px 12px', fontSize: 16, border: '1px solid #d8d2c0', borderRadius: 8, background: '#fff', color: '#1a1a18' };
const LABEL = { display: 'block', fontSize: 13.5, color: '#6a6558', marginBottom: 6 };
const LIGNE = { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '9px 0', borderBottom: '1px solid #e8e1cd', gap: 16 };

export default function CongeParentalCalc({ lang = 'fr' }) {
  const t = T[lang] || T.fr;
  const [brut, setBrut] = useState('');
  const [forme, setForme] = useState(FORMES[0].id);
  const r = simulerCongeParental({ brutMensuel: brut, forme });

  return (
    <section style={CADRE}>
      <h2 style={{ fontSize: 20, fontWeight: 800, margin: '0 0 6px', color: '#1a1a18' }}>{t.titre}</h2>
      <p style={{ fontSize: 14.5, color: '#6a6558', margin: '0 0 18px', lineHeight: 1.6 }}>{t.intro}</p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 14, marginBottom: 20 }}>
        <div>
          <label style={LABEL} htmlFor="cp-brut">{t.brut}</label>
          <input id="cp-brut" style={CHAMP} type="number" min="0" inputMode="numeric"
                 value={brut} onChange={(e) => setBrut(e.target.value)} placeholder="3000" />
        </div>
        <div>
          <label style={LABEL} htmlFor="cp-forme">{t.forme}</label>
          <select id="cp-forme" style={CHAMP} value={forme} onChange={(e) => setForme(e.target.value)}>
            {FORMES.map((f) => <option key={f.id} value={f.id}>{libelle(f, lang)}</option>)}
          </select>
        </div>
      </div>

      {!r && <p style={{ fontSize: 14.5, color: '#8a8578', margin: 0 }}>{t.vide}</p>}

      {r && (
        <div>
          <div style={LIGNE}>
            <span style={{ fontSize: 15, color: '#333330' }}>{t.duree}</span>
            <strong style={{ fontSize: 16, color: '#333330' }}>
              {r.dureeMoisMax} {t.mois} <span style={{ fontWeight: 400, color: '#6a6558', fontSize: 13 }}>· {t.fraction} {r.fractionMois}</span>
            </strong>
          </div>
          <div style={LIGNE}>
            <span style={{ fontSize: 15, color: '#333330' }}>{t.salaire}</span>
            <strong style={{ fontSize: 16, color: '#333330' }}>{euros(r.salaireMaintenu)}</strong>
          </div>

          {r.allocationBrute === null ? (
            <p style={{ fontSize: 14.5, color: '#8a5a00', background: '#fff7e0', border: '1px solid #e8d9a0',
                        borderRadius: 8, padding: '12px 14px', margin: '12px 0 0', lineHeight: 1.65 }}>
              {t.inconnu}
            </p>
          ) : (
            <>
              <div style={LIGNE}>
                <span style={{ fontSize: 15, color: '#333330' }}>{t.alloc}</span>
                <strong style={{ fontSize: 16, color: '#333330' }}>
                  {euros(r.allocationBrute)} <span style={{ fontWeight: 400, color: '#6a6558', fontSize: 13 }}>· {t.allocNet} {euros(r.allocationNette)}</span>
                </strong>
              </div>
              <div style={{ ...LIGNE, borderBottom: 'none' }}>
                <span style={{ fontSize: 15, color: '#333330' }}>{t.total}</span>
                <strong style={{ fontSize: 22, color: '#1a1a18' }}>{euros(r.totalBrut)}</strong>
              </div>
              <div style={{ ...LIGNE, borderBottom: 'none', paddingTop: 0 }}>
                <span style={{ fontSize: 14, color: '#6a6558' }}>{t.perte}</span>
                <strong style={{ fontSize: 15, color: r.perteBrute > 0 ? '#b45309' : '#15803d' }}>
                  {r.perteBrute > 0 ? '\u2212' : r.perteBrute < 0 ? '+' : ''}{euros(Math.abs(r.perteBrute))}
                </strong>
              </div>
            </>
          )}

          <p style={{ fontSize: 13, color: '#6a6558', margin: '14px 0 0', lineHeight: 1.65 }}>{t.cond}</p>
          <p style={{ fontSize: 12.5, color: '#8a8578', margin: '6px 0 0', lineHeight: 1.6 }}>{C.source}</p>
        </div>
      )}
    </section>
  );
}
